import { SettingsPermissions } from "@redotech/account-common/permissions";
import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { ClientUploader } from "@redotech/redo-merchant-app-common/client/upload";
import { InfoToCollect } from "@redotech/redo-return-core-common/return-flow-enums";
import { CustomerResponseState } from "@redotech/redo-return-ui/flows/chat-flow/customer-response";
import { containsImg } from "@redotech/redo-return-ui/flows/chat-flow/step-rich-text-util";
import { StepTypeDetailsProps } from "@redotech/redo-return-ui/flows/step";
import { LabeledInput } from "@redotech/redo-web/labeled-input";
import { SelectDropdown } from "@redotech/redo-web/select-dropdown";
import Quill from "quill";
import { memo, useRef } from "react";
import { StepRichTextEditor } from "./step-rich-text";

const options: InfoToCollect[] = [
  InfoToCollect.NAME,
  InfoToCollect.EMAIL,
  InfoToCollect.CUSTOM,
];

export const CustomerResponseDetails = memo(function CustomerResponseDetails({
  stepSelect,
  state,
  setState,
}: StepTypeDetailsProps<CustomerResponseState>) {
  const { t } = useTranslation();
  // TODO: Not sure what the right permission mix is
  const canEditSupportSettings = useHasPermission(
    SettingsPermissions.SET_SUPPORT_SETTINGS,
  );
  const canEditReturnsSettings = useHasPermission(
    SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
  );
  const canEditSettings = canEditSupportSettings || canEditReturnsSettings;

  const messageQuillRef = useRef<Quill | undefined>(undefined);

  const handleInfoToCollectChange = useHandler(
    (infoToCollect: InfoToCollect) => {
      setState((state) => ({ ...state, infoToCollect }));
    },
  );

  const handleMessageChange = useHandler(() => {
    if (!messageQuillRef.current) {
      return;
    }
    let message = messageQuillRef.current.getText();
    let messageHtml: string | undefined =
      messageQuillRef.current.getSemanticHTML();
    if (message === "\n") {
      message = "";
    }
    if (messageHtml === "<p></p>") {
      messageHtml = undefined;
    }

    if (messageHtml !== state.messageHtml || message !== state.message) {
      setState((state) => ({ ...state, message, messageHtml }));
    }
  });

  return (
    <ClientUploader>
      <LabeledInput label={t("Info to collect")}>
        <SelectDropdown
          disabled={!canEditSettings}
          options={options}
          value={state.infoToCollect}
          valueChange={handleInfoToCollectChange}
        >
          {(option) => {
            if (option === InfoToCollect.NAME) {
              return t("Full name");
            } else {
              return option === InfoToCollect.EMAIL ? t("Email") : t("Custom");
            }
          }}
        </SelectDropdown>
      </LabeledInput>
      <LabeledInput
        errors={
          containsImg(state.messageHtml) ? [t("Cannot contain images")] : []
        }
        label={t("Message to customer")}
      >
        <StepRichTextEditor
          htmlValue={state.messageHtml}
          onTextChange={handleMessageChange}
          quillRef={messageQuillRef}
          readOnly={!canEditSettings}
          textValue={state.message}
        />
      </LabeledInput>
      <LabeledInput
        errors={!state.next ? [t("Required")] : []}
        label={t("Next")}
      >
        {stepSelect({
          value: state.next,
          valueChange: (next) => setState({ ...state, next }),
        })}
      </LabeledInput>
    </ClientUploader>
  );
});
